import { type UseShareLinkReturn } from '@littlebird/ui-utils';
import { type ReactNode } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { IconButton, Text, XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';
import { handleExternalUrlClick } from '@/utils/platform';

import { ShareInvitePeople } from './ShareInvitePeople';

interface ShareAccessControlsProps {
  link: UseShareLinkReturn;
  idPrefix: string;
  linkFooter?: ReactNode;
  className?: string;
}

const ToggleOption = ({
  id,
  icon: Icon,
  title,
  description,
  selected,
  loading,
  disabled,
  onClick,
}: {
  id: string;
  icon: LBIcon;
  title: string;
  description: string;
  selected: boolean;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    id={id}
    type="button"
    aria-pressed={selected}
    aria-busy={loading}
    disabled={disabled}
    className={cn(
      'flex items-center gap-2.5 py-2.5 text-left transition-colors',
      '-mx-4 px-4',
      selected ? 'bg-surface-active' : 'hover:bg-surface-hover'
    )}
    onClick={onClick}
  >
    <XStack
      center
      className="size-7 shrink-0 rounded-md border border-border bg-background-subtle"
    >
      <Icon className="size-3.5 text-foreground-muted" />
    </XStack>
    <YStack className="flex-1 min-w-0">
      <Text size="sm" fontWeight="medium" as="span">
        {title}
      </Text>
      <Text size="xs" variant="muted" as="span">
        {description}
      </Text>
    </YStack>
    {selected && !loading && (
      <Icons.check className="size-4 text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue shrink-0" />
    )}
    {loading && (
      <Icons.loader className="size-3.5 animate-spin text-foreground-muted shrink-0" />
    )}
  </button>
);

const OptionDivider = () => <div className="-mx-4 h-px bg-border" />;

/**
 * Presentational private/team/public toggle + share-link row, driven entirely by `useShareLink`
 * state. Shared by the chat and published-site share surfaces; surface-specific extras (chat's
 * new-messages banner, social buttons) are passed via `linkFooter`.
 */
export const ShareAccessControls = ({
  link,
  idPrefix,
  linkFooter,
  className,
}: ShareAccessControlsProps) => {
  const {
    url,
    shareStatus,
    copied,
    isCheckingStatus,
    isCreating,
    creatingVisibility,
    selectedVisibility,
    showTeam,
    showPublic,
    showPeople,
    invitedEmails,
    isLoading,
    isRevoking,
    handleCreateOrUpdateLink,
    handleRevoke,
    handleCopyLink,
    handleAddInvite,
    handleRemoveInvite,
  } = link;

  if (isCheckingStatus) {
    return (
      <XStack
        className={cn('no-record items-center justify-center p-3', className)}
      >
        <Icons.loader
          size={14}
          className="animate-spin text-foreground-subtle"
        />
      </XStack>
    );
  }

  const effectiveVisibility =
    creatingVisibility ?? (isRevoking ? 'private' : selectedVisibility);

  const linkReady = url && shareStatus?.isShared && !isCreating && !isRevoking;
  const linkPanel = linkReady ? (
    <YStack className="-mx-4 gap-2 bg-surface-active px-4 pb-2.5">
      <XStack className="gap-1.5 border border-border-strong rounded-md p-1.5 w-full items-center justify-between bg-surface text-foreground overflow-hidden">
        <Text
          size="xs"
          className="flex-1 min-w-0 truncate px-1.5 select-text"
          data-testid={`${idPrefix}-url`}
        >
          {url}
        </Text>
        <XStack className="gap-0.5 items-center shrink-0">
          <IconButton
            size="xs"
            icon={Icons.externalLink}
            tooltip="Open link"
            aria-label="Open link"
            onClick={() => handleExternalUrlClick(url)}
          />
          <IconButton
            size="xs"
            icon={copied ? Icons.circleCheck : Icons.copy}
            tooltip={copied ? 'Copied' : 'Copy link'}
            aria-label="Copy link"
            onClick={handleCopyLink}
          />
        </XStack>
      </XStack>
      {linkFooter}
    </YStack>
  ) : null;

  const panelAttached =
    (showPeople && effectiveVisibility === 'people') ||
    (showTeam && effectiveVisibility === 'team') ||
    (showPublic && effectiveVisibility === 'public');

  return (
    <YStack className={cn('no-record', className)}>
      <OptionDivider />
      <ToggleOption
        id={`${idPrefix}-private-toggle`}
        icon={Icons.lock}
        title="Private"
        description="Only you have access"
        selected={effectiveVisibility === 'private'}
        loading={isRevoking}
        disabled={isLoading}
        onClick={() => {
          if (selectedVisibility !== 'private' && !isLoading) handleRevoke();
        }}
      />
      {showPeople && (
        <>
          <OptionDivider />
          <ToggleOption
            id={`${idPrefix}-people-toggle`}
            icon={Icons.mail}
            title="Only invited people"
            description="People you invite by email can view"
            selected={effectiveVisibility === 'people'}
            loading={creatingVisibility === 'people'}
            disabled={isLoading}
            onClick={() => {
              if (selectedVisibility !== 'people' && !isLoading) {
                handleCreateOrUpdateLink('people');
              }
            }}
          />
          {effectiveVisibility === 'people' && linkReady && (
            <ShareInvitePeople
              idPrefix={idPrefix}
              invitedEmails={invitedEmails}
              onAdd={handleAddInvite}
              onRemove={handleRemoveInvite}
            />
          )}
          {effectiveVisibility === 'people' && linkPanel}
        </>
      )}
      {showTeam && (
        <>
          <OptionDivider />
          <ToggleOption
            id={`${idPrefix}-team-toggle`}
            icon={Icons.attendees}
            title="Team members"
            description="Only members of your team can view"
            selected={effectiveVisibility === 'team'}
            loading={creatingVisibility === 'team'}
            disabled={isLoading}
            onClick={() => {
              if (selectedVisibility !== 'team' && !isLoading) {
                handleCreateOrUpdateLink('team');
              }
            }}
          />
          {effectiveVisibility === 'team' && linkPanel}
        </>
      )}
      {showPublic && (
        <>
          <OptionDivider />
          <ToggleOption
            id={`${idPrefix}-public-toggle`}
            icon={Icons.webSearch}
            title="Public access"
            description="Anyone with the link can view"
            selected={effectiveVisibility === 'public'}
            loading={creatingVisibility === 'public'}
            disabled={isLoading}
            onClick={() => {
              if (selectedVisibility !== 'public' && !isLoading) {
                handleCreateOrUpdateLink('public');
              }
            }}
          />
          {effectiveVisibility === 'public' && linkPanel}
        </>
      )}
      {!panelAttached && linkPanel}
    </YStack>
  );
};
